import type { ChartData, ChartOptions } from 'chart.js';
import { BURBANK_GEOID } from '../../eia861UtilityElectricity/bwpObservations';
import type { CanonicalEnergyActivityYear } from './types';
import { activityMtCo2e, experimentalEmissionsForSector } from './experimentalEmissions';
import {
  EXPERIMENTAL_ELECTRICITY_EMISSION_TOOLTIP,
  EXPERIMENTAL_GAS_EMISSION_TOOLTIP,
  experimentalEmissionFactorLabel,
} from './experimentalEmissionFactors';
import { formatActivityUnits, formatMtCo2e } from './formatExperimentValues';
import { formatElectricityTooltipPrimary, formatGwhAxisTickFromKwh } from '../../../utils/formatElectricityGwh';
import {
  buildElecNonresidentialChartSegments,
  elecNonresYearTooltipLines,
} from './elecNonresidentialChartSegments';
import { buildElecTotalChartSegments } from './elecTotalChartSegments';
import { buildGasTotalChartSegments } from './gasTotalChartSegments';
import {
  capTotalReferenceBarValue,
  type CapTotalMetric,
} from './capTotalReference';
import {
  elecTotalYearTooltipLines,
  gasTotalYearTooltipLinesRevised,
} from './capTotalChartTooltips';
import {
  activityDataSourceLabelForEstimate,
  provenanceStackLabel,
} from './activityDataSourceLabels';
import {
  burbankSoCalGasCommercialOverlayYear,
  burbankSoCalGasResidentialOverlayYear,
} from './burbankSoCalGasOverlayYears';
import { buildBurbankElecComIndStackSegments } from './burbankElecComIndStackSegments';
import { buildGasComIndStackSegments } from './gasComIndStackSegments';
import { BH_EXPERIMENT_GEOID } from './bhSourceControls';
import { experimentalFigureTitlePlugins } from '../../../utils/chartFormatting';

export type { ChartDisplayMode } from './elecNonresidentialChartSegments';

export type Rev2ChartViewKey =
  | 'elec_total'
  | 'elec_residential'
  | 'elec_nonresidential'
  | 'elec_commercial_industrial'
  | 'gas_total'
  | 'gas_commercial_industrial'
  | 'gas_residential'
  | 'gas_nonresidential';

export type Rev2ChartBundleOptions = {
  /** Total natural gas chart: add industrial segment when reported in canonical rows. */
  gasTotalIncludeIndustrial?: boolean;
  /** Shown as in-chart subtitle under the figure title (e.g. city name). */
  jurisdictionName?: string;
};

export const REV2_CHART_OPTIONS: Array<{ value: Rev2ChartViewKey; label: string }> = [
  { value: 'elec_total', label: 'Total electricity' },
  { value: 'elec_residential', label: 'Residential electricity' },
  { value: 'elec_nonresidential', label: 'Nonresidential electricity' },
  { value: 'gas_total', label: 'Total natural gas' },
  { value: 'gas_residential', label: 'Residential natural gas' },
  { value: 'gas_nonresidential', label: 'Nonresidential natural gas' },
];

/** Burbank Analysis: user-facing “Commercial” wording + EIA commercial/industrial stack chart. */
export const BURBANK_REV2_CHART_OPTIONS: Array<{ value: Rev2ChartViewKey; label: string }> = [
  { value: 'elec_total', label: 'Total electricity' },
  { value: 'elec_residential', label: 'Residential electricity' },
  { value: 'elec_nonresidential', label: 'Commercial electricity' },
  {
    value: 'elec_commercial_industrial',
    label: 'Commercial + industrial electricity (stacked)',
  },
  {
    value: 'gas_commercial_industrial',
    label: 'Commercial + industrial natural gas (stacked)',
  },
  { value: 'gas_total', label: 'Total natural gas' },
  { value: 'gas_residential', label: 'Residential natural gas' },
  { value: 'gas_nonresidential', label: 'Commercial natural gas' },
];

/** Beverly Hills: SoCalGas commercial category (not a separate industrial gas series). */
export const BH_REV2_CHART_OPTIONS: Array<{ value: Rev2ChartViewKey; label: string }> =
  REV2_CHART_OPTIONS.map((o) =>
    o.value === 'gas_nonresidential' ? { ...o, label: 'Commercial natural gas' } : o
  );

function normalizeExperimentGeoid(geoid: string | null | undefined): string | null {
  if (!geoid) return null;
  return geoid.length === 6 ? `0${geoid}` : geoid;
}

export function rev2ChartOptionsForLocation(
  isBurbankEiaPilot: boolean,
  geoid?: string | null
): Array<{ value: Rev2ChartViewKey; label: string }> {
  if (isBurbankEiaPilot) return BURBANK_REV2_CHART_OPTIONS;
  if (normalizeExperimentGeoid(geoid) === BH_EXPERIMENT_GEOID) return BH_REV2_CHART_OPTIONS;
  return REV2_CHART_OPTIONS;
}

export function rev2ChartLabelForView(
  view: Rev2ChartViewKey,
  isBurbankEiaPilot: boolean,
  geoid?: string | null
): string {
  const opts = rev2ChartOptionsForLocation(isBurbankEiaPilot, geoid);
  return opts.find((o) => o.value === view)?.label ?? view;
}

export const STACK_EXPERIMENTAL = 'experimental';
export const STACK_CAP_REFERENCE = 'cap_reference';

export const CAP_REFERENCE_BAR_STYLE = {
  backgroundColor: '#e5e7eb',
  borderColor: '#1f2937',
  borderWidth: 2,
} as const;

export const EIA861_CAP_YEAR_REFERENCE_LINE_LABEL = 'EIA-861 BWP (same year as published CAP bar)';

/** Burbank residential gas: ClimatePlans / SoCal Gas estimate overlaid on published CAP years. */
export const CLIMATEPLANS_GAS_CAP_YEAR_REFERENCE_LINE_LABEL =
  'ClimatePlans SoCal Gas (residential)';

/** Burbank nonresidential gas: SoCal Gas commercial category in ClimatePlans (2021–2024 filings). */
export const CLIMATEPLANS_GAS_COMMERCIAL_CAP_YEAR_REFERENCE_LINE_LABEL =
  'ClimatePlans SoCal Gas commercial';

/** Burbank harmonized total charts (CAP + utility blend per sector rules). */
export const HARMONIZED_TOTAL_RESIDENTIAL_LABEL = 'Residential';
export const HARMONIZED_TOTAL_NONRESIDENTIAL_LABEL = 'Nonresidential';

export const BURBANK_HARMONIZED_COMMERCIAL_LABEL = 'Commercial';

export const REV2_PROVENANCE_COLORS = {
  capAnchor: '#1e40af',
  utilityObserved: '#166534',
  interpolated: '#3b82f6',
  extrapolated: '#8b5cf6',
  gasAdditional: '#86efac',
  modeledCommercial: '#60a5fa',
  elecCommercial: '#166534',
  elecIndustrial: '#ca8a04',
  elecUndivided: '#64748b',
  gasResidential: '#1e40af',
  gasNonresidential: '#166534',
  legacyLine: '#9ca3af',
} as const;

function provenanceBucket(row: CanonicalEnergyActivityYear, sector: 'residential' | 'nonresidential' | 'total'): string {
  const est =
    sector === 'residential' ? row.residential : sector === 'nonresidential' ? row.nonresidential : row.total;
  switch (est.coverage) {
    case 'complete_observed':
      return 'utilityObserved';
    case 'cap_published':
    case 'cap_derived_total':
      return 'capAnchor';
    case 'interpolated':
      return 'interpolated';
    case 'estimated_complete':
      return 'interpolated';
    case 'partial_observed_lower_bound':
      return 'utilityObserved';
    default:
      return 'extrapolated';
  }
}

function legacyComparisonValue(row: CanonicalEnergyActivityYear, view: Rev2ChartViewKey): number | null {
  switch (view) {
    case 'elec_residential':
    case 'gas_residential':
      return row.legacyPhysicalResidential ?? null;
    case 'elec_nonresidential':
      return row.legacyPhysicalCommercial ?? null;
    case 'gas_nonresidential':
      return row.legacyPhysicalCommercial ?? null;
    default:
      return null;
  }
}

export function legacyComparisonLabel(view: Rev2ChartViewKey): string {
  switch (view) {
    case 'elec_residential':
    case 'gas_residential':
      return 'Legacy ClimatePlans residential physical series';
    case 'elec_nonresidential':
      return 'Legacy ClimatePlans commercial-only model — industrial excluded';
    case 'gas_nonresidential':
      return 'Legacy ClimatePlans commercial physical model (comparison)';
    default:
      return 'Legacy comparison';
  }
}

function formatWhole(n: number): number {
  return Math.round(n);
}

/** True when a dataset should appear in the chart legend (has at least one plotted value). */
export function datasetHasPlottedValues(
  data: ReadonlyArray<number | null | undefined>,
  chartType?: string
): boolean {
  if (chartType === 'line') {
    return data.some((v) => v != null && Number.isFinite(v));
  }
  return data.some((v) => v != null && Number.isFinite(v) && v !== 0);
}

export function filterDatasetsWithPlottedValues<T extends ChartData<'bar' | 'line'>['datasets']>(
  datasets: T
): T {
  return datasets.filter((ds) =>
    datasetHasPlottedValues((ds.data ?? []) as Array<number | null>, ds.type as string)
  ) as T;
}

function capMetricForView(view: Rev2ChartViewKey): CapTotalMetric | null {
  if (view === 'elec_total') return 'electricity';
  if (view === 'gas_total') return 'natural_gas';
  return null;
}

export function buildRev2ChartBundle(
  rows: CanonicalEnergyActivityYear[],
  view: Rev2ChartViewKey,
  mode: import('./elecNonresidentialChartSegments').ChartDisplayMode,
  bundleOptions?: Rev2ChartBundleOptions
): { data: ChartData<'bar' | 'line'>; options: ChartOptions<'bar' | 'line'>; legacyLabel: string } {
  const sector: 'residential' | 'nonresidential' | 'total' =
    view === 'elec_total' || view === 'gas_total'
      ? 'total'
      : view === 'elec_residential' || view === 'gas_residential'
        ? 'residential'
        : 'nonresidential';

  const labels = rows.map((r) => r.year);
  const isBurbankEiaPilot = rows[0]?.geoid === BURBANK_GEOID;
  const isBurbankComIndStack = view === 'elec_commercial_industrial' && isBurbankEiaPilot;
  const isGasComIndStack = view === 'gas_commercial_industrial';
  const isBhExperiment = rows[0]?.geoid === BH_EXPERIMENT_GEOID;
  const isBhCommercialGasChart = isBhExperiment && view === 'gas_nonresidential';
  const isGasTotal = view === 'gas_total';
  const gasTotalIncludeIndustrial = Boolean(bundleOptions?.gasTotalIncludeIndustrial);
  const isElecNonres = view === 'elec_nonresidential';
  const isElecNonresComponentChart = isElecNonres && !isBurbankEiaPilot;
  const isElecTotal = view === 'elec_total';
  const isTotalWithCapReference = isGasTotal || isElecTotal;
  const usesComponentStack =
    isElecNonresComponentChart ||
    isGasTotal ||
    isElecTotal ||
    isBurbankComIndStack ||
    isGasComIndStack;
  const harmonizedNonresLabel = isBurbankEiaPilot
    ? BURBANK_HARMONIZED_COMMERCIAL_LABEL
    : HARMONIZED_TOTAL_NONRESIDENTIAL_LABEL;
  const capMetric = capMetricForView(view);
  const legacyLabel =
    isBurbankEiaPilot && view.startsWith('elec')
      ? 'Previous ClimatePlans estimate'
      : legacyComparisonLabel(view);

  const experimentalMain: number[] = [];
  const elecCommercial: number[] = [];
  const elecIndustrial: number[] = [];
  const elecOther: number[] = [];
  const elecUndivided: number[] = [];
  const gasResidential: number[] = [];
  const gasTotalStack: number[] = [];
  const gasTotalIndustrial: Array<number | null> = [];
  const gasComIndCommercial: Array<number | null> = [];
  const gasComIndIndustrial: Array<number | null> = [];
  const elecTotalResidential: number[] = [];
  const elecTotalNonres: number[] = [];
  const elecComIndCommercial: Array<number | null> = [];
  const elecComIndIndustrial: Array<number | null> = [];
  const capReferenceTotal: Array<number | null> = [];
  const capAnchor: number[] = [];
  const utilityObs: number[] = [];
  const interpolated: number[] = [];
  const extrapolated: number[] = [];
  const capSectorReference: Array<number | null> = [];
  const chartFuel: 'electricity' | 'natural_gas' = view.startsWith('elec') ? 'electricity' : 'natural_gas';
  const provenanceLabelOptions = {
    isBurbankEia: isBurbankEiaPilot,
    fuel: chartFuel,
    isBhExperiment,
    isBhCommercialGasChart,
  };

  for (const row of rows) {
    const est =
      sector === 'residential' ? row.residential : sector === 'nonresidential' ? row.nonresidential : row.total;
    const val =
      mode === 'emissions'
        ? experimentalEmissionsForSector(row, sector) ?? 0
        : est.value != null
          ? formatWhole(est.value)
          : 0;
    if (isBurbankComIndStack) {
      const seg = buildBurbankElecComIndStackSegments(row, mode);
      elecComIndCommercial.push(seg.commercial);
      elecComIndIndustrial.push(seg.industrial);
      const stackTotal =
        (seg.commercial ?? 0) + (seg.industrial ?? 0);
      experimentalMain.push(stackTotal > 0 ? stackTotal : 0);
    } else if (isGasComIndStack) {
      const seg = buildGasComIndStackSegments(row, mode);
      gasComIndCommercial.push(seg.commercial);
      gasComIndIndustrial.push(seg.industrial);
      const stackTotal = (seg.commercial ?? 0) + (seg.industrial ?? 0);
      experimentalMain.push(stackTotal > 0 ? stackTotal : 0);
    } else if (isElecNonresComponentChart) {
      const seg = buildElecNonresidentialChartSegments(row, mode);
      elecCommercial.push(seg.commercial);
      elecIndustrial.push(seg.industrial);
      elecOther.push(seg.otherNonresidential);
      elecUndivided.push(seg.undividedNonresidential);
      experimentalMain.push(seg.canonicalTotal);
    } else if (isElecTotal) {
      const seg = buildElecTotalChartSegments(row, mode);
      elecTotalResidential.push(seg.residential);
      elecTotalNonres.push(seg.nonresidential);
      experimentalMain.push(seg.canonicalTotal);
    } else if (isGasTotal) {
      const seg = buildGasTotalChartSegments(row, mode, {
        includeIndustrial: gasTotalIncludeIndustrial,
      });
      gasResidential.push(seg.residential);
      gasTotalStack.push(seg.commercialCategory);
      gasTotalIndustrial.push(seg.industrialCategory);
      experimentalMain.push(seg.canonicalTotal);
    } else {
      experimentalMain.push(val);
    }

    if (capMetric) {
      capReferenceTotal.push(capTotalReferenceBarValue(capMetric, row.year, mode));
    }

    if (!usesComponentStack) {
      const bucket = provenanceBucket(row, sector);
      capAnchor.push(bucket === 'capAnchor' ? val : 0);
      utilityObs.push(bucket === 'utilityObserved' ? val : 0);
      interpolated.push(bucket === 'interpolated' ? val : 0);
      extrapolated.push(bucket === 'extrapolated' ? val : 0);
    }

    capSectorReference.push(null);
  }

  const hasCapSectorReference =
    !isBurbankEiaPilot && capSectorReference.some((v) => v != null && Number.isFinite(v));

  /** Burbank Analysis uses blended canonical values only — no grey CAP outline bars or legacy overlay. */
  const showLegacyLine = !isTotalWithCapReference && !isBurbankEiaPilot;
  const showCapTotalReferenceBar = isTotalWithCapReference && !isBurbankEiaPilot;
  const legacyLine = showLegacyLine
    ? rows.map((r) => {
        const v = legacyComparisonValue(r, view);
        if (v == null) return null;
        if (mode === 'emissions') {
          const fuel = view.startsWith('elec') ? 'electricity' : 'natural_gas';
          const factor = fuel === 'electricity' ? 0.00024 : 0.005313;
          return formatWhole(v * factor);
        }
        return formatWhole(v);
      })
    : [];

  const showEia861CapYearReferenceLine =
    isBurbankEiaPilot && chartFuel === 'electricity' && !usesComponentStack && sector !== 'total';

  const eia861CapYearReferenceLine = showEia861CapYearReferenceLine
    ? rows.map((r) => {
        const est =
          sector === 'residential' ? r.residential : sector === 'nonresidential' ? r.nonresidential : null;
        if (!est || est.coverage !== 'cap_published') return null;
        const eiaKwh =
          sector === 'residential'
            ? r.eia861ReferenceResidentialKwh
            : r.eia861ReferenceNonresidentialKwh;
        if (eiaKwh == null || !Number.isFinite(eiaKwh)) return null;
        if (mode === 'emissions') {
          const mt = activityMtCo2e(
            {
              value: eiaKwh,
              unit: 'kWh',
              coverage: 'complete_observed',
              sourceClass: 'utility_observed',
              methodId: 'burbank_eia861_overlay',
              sourceNote: EIA861_CAP_YEAR_REFERENCE_LINE_LABEL,
              includedCategories: [],
              missingCategories: [],
            },
            'electricity'
          );
          return mt != null ? formatWhole(mt) : null;
        }
        return formatWhole(eiaKwh);
      })
    : [];

  const hasEia861CapYearReferenceLine = eia861CapYearReferenceLine.some(
    (v) => v != null && Number.isFinite(v)
  );

  /** Same white-dot pattern as electricity: ClimatePlans SoCal Gas on CAP inventory years. */
  const showClimatePlansGasCapYearReferenceLine =
    isBurbankEiaPilot && view === 'gas_residential';

  const climatePlansGasCapYearReferenceLine = showClimatePlansGasCapYearReferenceLine
    ? rows.map((r) => {
        if (!burbankSoCalGasResidentialOverlayYear(r.year)) return null;
        const therms = r.legacyPhysicalResidential;
        if (therms == null || !Number.isFinite(therms)) return null;
        if (mode === 'emissions') {
          const mt = activityMtCo2e(
            {
              value: therms,
              unit: 'therms',
              coverage: 'complete_observed',
              sourceClass: 'utility_observed',
              methodId: 'burbank_gas_res_socalgas_climateplans_overlay',
              sourceNote: CLIMATEPLANS_GAS_CAP_YEAR_REFERENCE_LINE_LABEL,
              includedCategories: [],
              missingCategories: [],
            },
            'natural_gas'
          );
          return mt != null ? formatWhole(mt) : null;
        }
        return formatWhole(therms);
      })
    : [];

  const hasClimatePlansGasCapYearReferenceLine = climatePlansGasCapYearReferenceLine.some(
    (v) => v != null && Number.isFinite(v)
  );

  const showClimatePlansGasCommercialCapYearReferenceLine =
    isBurbankEiaPilot && view === 'gas_nonresidential';

  const climatePlansGasCommercialCapYearReferenceLine = showClimatePlansGasCommercialCapYearReferenceLine
    ? rows.map((r) => {
        if (!burbankSoCalGasCommercialOverlayYear(r.year)) return null;
        const therms = r.legacyPhysicalCommercial;
        if (therms == null || !Number.isFinite(therms)) return null;
        if (mode === 'emissions') {
          const mt = activityMtCo2e(
            {
              value: therms,
              unit: 'therms',
              coverage: 'complete_observed',
              sourceClass: 'utility_observed',
              methodId: 'burbank_gas_com_socalgas_climateplans_overlay',
              sourceNote: CLIMATEPLANS_GAS_COMMERCIAL_CAP_YEAR_REFERENCE_LINE_LABEL,
              includedCategories: [],
              missingCategories: [],
            },
            'natural_gas'
          );
          return mt != null ? formatWhole(mt) : null;
        }
        return formatWhole(therms);
      })
    : [];

  const hasClimatePlansGasCommercialCapYearReferenceLine =
    climatePlansGasCommercialCapYearReferenceLine.some((v) => v != null && Number.isFinite(v));

  const isElecActivity = view.startsWith('elec') && mode === 'activity';
  const yLabel =
    mode === 'emissions'
      ? 'MT CO2e (experimental)'
      : view.startsWith('elec')
        ? 'GWh'
        : 'Therms';

  const datasets: ChartData<'bar' | 'line'>['datasets'] = [];

  if (isBurbankComIndStack) {
    datasets.push(
      {
        type: 'bar',
        label: 'Commercial',
        data: elecComIndCommercial,
        backgroundColor: REV2_PROVENANCE_COLORS.elecCommercial,
        borderColor: '#1e3a5f',
        borderWidth: 1,
        stack: 'burbank_com_ind',
        order: 2,
      },
      {
        type: 'bar',
        label: 'Industrial',
        data: elecComIndIndustrial,
        backgroundColor: REV2_PROVENANCE_COLORS.elecIndustrial,
        borderColor: '#1e3a5f',
        borderWidth: 1,
        stack: 'burbank_com_ind',
        order: 2,
      }
    );
  } else if (isGasComIndStack) {
    datasets.push(
      {
        type: 'bar',
        label: isBurbankEiaPilot ? 'Commercial' : 'Commercial category',
        data: gasComIndCommercial,
        backgroundColor: REV2_PROVENANCE_COLORS.gasNonresidential,
        borderColor: '#14532d',
        borderWidth: 1,
        stack: 'gas_com_ind',
        order: 2,
      },
      {
        type: 'bar',
        label: 'Industrial',
        data: gasComIndIndustrial,
        backgroundColor: REV2_PROVENANCE_COLORS.elecIndustrial,
        borderColor: '#1e3a5f',
        borderWidth: 1,
        stack: 'gas_com_ind',
        order: 2,
      }
    );
  } else if (isGasTotal) {
    const totalResLabel = isBurbankEiaPilot ? HARMONIZED_TOTAL_RESIDENTIAL_LABEL : 'Experimental residential';
    const totalNonresLabel = isBurbankEiaPilot ? harmonizedNonresLabel : 'Experimental commercial category';
    const totalComLabel = gasTotalIncludeIndustrial
      ? isBurbankEiaPilot
        ? 'Commercial'
        : 'Commercial category'
      : totalNonresLabel;
    datasets.push(
      {
        type: 'bar',
        label: totalResLabel,
        data: gasResidential,
        backgroundColor: REV2_PROVENANCE_COLORS.gasResidential,
        borderColor: '#1e3a5f',
        borderWidth: 1,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: totalComLabel,
        data: gasTotalStack,
        backgroundColor: REV2_PROVENANCE_COLORS.gasNonresidential,
        borderColor: '#14532d',
        borderWidth: 1,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      ...(gasTotalIncludeIndustrial
        ? [
            {
              type: 'bar' as const,
              label: 'Industrial',
              data: gasTotalIndustrial,
              backgroundColor: REV2_PROVENANCE_COLORS.elecIndustrial,
              borderColor: '#1e3a5f',
              borderWidth: 1,
              stack: STACK_EXPERIMENTAL,
              order: 2,
            },
          ]
        : []),
      ...(showCapTotalReferenceBar
        ? [
            {
              type: 'bar' as const,
              label: 'CAP total',
              data: capReferenceTotal,
              ...CAP_REFERENCE_BAR_STYLE,
              stack: STACK_CAP_REFERENCE,
              order: 2,
            },
          ]
        : []),
    );
  } else if (isElecTotal) {
    const totalResLabel = isBurbankEiaPilot ? HARMONIZED_TOTAL_RESIDENTIAL_LABEL : 'Experimental residential';
    const totalNonresLabel = isBurbankEiaPilot ? harmonizedNonresLabel : 'Experimental nonresidential';
    datasets.push(
      {
        type: 'bar',
        label: totalResLabel,
        data: elecTotalResidential,
        backgroundColor: REV2_PROVENANCE_COLORS.gasResidential,
        borderColor: '#1e3a5f',
        borderWidth: 1,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: totalNonresLabel,
        data: elecTotalNonres,
        backgroundColor: REV2_PROVENANCE_COLORS.gasNonresidential,
        borderColor: '#14532d',
        borderWidth: 1,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      ...(showCapTotalReferenceBar
        ? [
            {
              type: 'bar' as const,
              label: 'CAP total',
              data: capReferenceTotal,
              ...CAP_REFERENCE_BAR_STYLE,
              stack: STACK_CAP_REFERENCE,
              order: 2,
            },
          ]
        : []),
    );
  } else if (isElecNonresComponentChart) {
    datasets.push(
      {
        type: 'bar',
        label: isBurbankEiaPilot ? 'EIA-861 commercial component' : 'Commercial',
        data: elecCommercial,
        backgroundColor: REV2_PROVENANCE_COLORS.elecCommercial,
        borderColor: '#1e3a5f',
        borderWidth: 1,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: isBurbankEiaPilot ? 'EIA-861 industrial component' : 'Industrial',
        data: elecIndustrial,
        backgroundColor: REV2_PROVENANCE_COLORS.elecIndustrial,
        borderColor: '#1e3a5f',
        borderWidth: 1,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: 'Other nonresidential',
        data: elecOther,
        backgroundColor: '#94a3b8',
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: isBurbankEiaPilot
          ? 'EIA-861 nonresidential total'
          : 'Nonresidential total — sector split unavailable',
        data: elecUndivided,
        backgroundColor: REV2_PROVENANCE_COLORS.elecUndivided,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      }
    );
  } else {
    datasets.push(
      {
        type: 'bar',
        label: provenanceStackLabel('capAnchor', provenanceLabelOptions),
        data: capAnchor,
        backgroundColor: REV2_PROVENANCE_COLORS.capAnchor,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: provenanceStackLabel('utilityObserved', provenanceLabelOptions),
        data: utilityObs,
        backgroundColor: REV2_PROVENANCE_COLORS.utilityObserved,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: provenanceStackLabel('interpolated', provenanceLabelOptions),
        data: interpolated,
        backgroundColor: REV2_PROVENANCE_COLORS.interpolated,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      },
      {
        type: 'bar',
        label: provenanceStackLabel('extrapolated', provenanceLabelOptions),
        data: extrapolated,
        backgroundColor: REV2_PROVENANCE_COLORS.extrapolated,
        stack: STACK_EXPERIMENTAL,
        order: 2,
      }
    );
  }

  if (hasCapSectorReference) {
    const capSectorLabel =
      view === 'elec_nonresidential'
        ? 'Published CAP commercial (not EIA nonresidential)'
        : view === 'elec_residential'
          ? 'Published CAP residential'
          : view === 'gas_nonresidential'
            ? 'Published CAP non-residential gas'
            : 'Published CAP residential gas';
    datasets.push({
      type: 'bar',
      label: capSectorLabel,
      data: capSectorReference,
      ...CAP_REFERENCE_BAR_STYLE,
      stack: STACK_CAP_REFERENCE,
      order: 2,
    });
  }

  if (showLegacyLine) {
    datasets.push({
      type: 'line',
      label: legacyLabel,
      data: legacyLine,
      borderColor: REV2_PROVENANCE_COLORS.legacyLine,
      borderDash: [6, 4],
      pointRadius: 0,
      fill: false,
      order: 1,
    });
  }

  if (hasEia861CapYearReferenceLine) {
    datasets.push({
      type: 'line',
      label: EIA861_CAP_YEAR_REFERENCE_LINE_LABEL,
      data: eia861CapYearReferenceLine,
      borderColor: REV2_PROVENANCE_COLORS.legacyLine,
      borderDash: [5, 5],
      pointRadius: 5,
      pointBackgroundColor: '#f9fafb',
      pointBorderColor: REV2_PROVENANCE_COLORS.legacyLine,
      pointBorderWidth: 2,
      spanGaps: false,
      fill: false,
      order: 0,
    });
  }

  if (hasClimatePlansGasCapYearReferenceLine) {
    datasets.push({
      type: 'line',
      label: CLIMATEPLANS_GAS_CAP_YEAR_REFERENCE_LINE_LABEL,
      data: climatePlansGasCapYearReferenceLine,
      borderColor: REV2_PROVENANCE_COLORS.legacyLine,
      borderDash: [5, 5],
      pointRadius: 5,
      pointBackgroundColor: '#f9fafb',
      pointBorderColor: REV2_PROVENANCE_COLORS.legacyLine,
      pointBorderWidth: 2,
      spanGaps: false,
      fill: false,
      order: 0,
    });
  }

  if (hasClimatePlansGasCommercialCapYearReferenceLine) {
    datasets.push({
      type: 'line',
      label: CLIMATEPLANS_GAS_COMMERCIAL_CAP_YEAR_REFERENCE_LINE_LABEL,
      data: climatePlansGasCommercialCapYearReferenceLine,
      borderColor: REV2_PROVENANCE_COLORS.legacyLine,
      borderDash: [5, 5],
      pointRadius: 5,
      pointBackgroundColor: '#f9fafb',
      pointBorderColor: REV2_PROVENANCE_COLORS.legacyLine,
      pointBorderWidth: 2,
      spanGaps: false,
      fill: false,
      order: 0,
    });
  }

  const rowByYear = new Map(rows.map((r) => [r.year, r]));
  const visibleDatasets = filterDatasetsWithPlottedValues(datasets);

  return {
    legacyLabel,
    data: { labels, datasets: visibleDatasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction:
        usesComponentStack || hasCapSectorReference ? { mode: 'index', intersect: false } : undefined,
      plugins: {
        legend: { position: 'bottom' },
        ...experimentalFigureTitlePlugins(
          rev2ChartLabelForView(view, isBurbankEiaPilot, rows[0]?.geoid),
          bundleOptions?.jurisdictionName ?? ''
        ),
        tooltip: {
          callbacks: {
            ...(isBurbankComIndStack
              ? {
                  filter: () => true,
                  label: (ctx) => {
                    const v = ctx.parsed.y;
                    if (v == null || !Number.isFinite(v)) return '';
                    const unit = mode === 'emissions' ? 'MT CO2e' : 'kWh';
                    return `${ctx.dataset.label}: ${Number(v).toLocaleString()} ${unit}`;
                  },
                  afterBody: (items) => {
                    const year = Number(items[0]?.label);
                    const row = rowByYear.get(year);
                    if (!row) return ['No EIA commercial/industrial component data for this year.'];
                    const seg = buildBurbankElecComIndStackSegments(row, mode);
                    if (seg.commercial == null && seg.industrial == null) {
                      return ['No stacked commercial/industrial components for this year.'];
                    }
                    return [
                      `Data source: ${activityDataSourceLabelForEstimate(row.nonresidential, {
                        treatUtilityAsEia: true,
                      })}`,
                      row.nonresidential.sourceNote,
                    ];
                  },
                }
              : isGasComIndStack
                ? {
                    filter: () => true,
                    label: (ctx) => {
                      const v = ctx.parsed.y;
                      if (v == null || !Number.isFinite(v)) return '';
                      const unit = mode === 'emissions' ? 'MT CO2e' : 'therms';
                      return `${ctx.dataset.label}: ${Number(v).toLocaleString()} ${unit}`;
                    },
                    afterBody: (items) => {
                      const year = Number(items[0]?.label);
                      const row = rowByYear.get(year);
                      if (!row) return ['No commercial/industrial gas data for this year.'];
                      const seg = buildGasComIndStackSegments(row, mode);
                      if (seg.commercial == null && seg.industrial == null) {
                        return ['No stacked commercial/industrial gas for this year.'];
                      }
                      const lines = [
                        `Data source: ${activityDataSourceLabelForEstimate(row.commercial, {
                          treatUtilityAsEia: false,
                        })}`,
                        row.commercial.sourceNote,
                      ];
                      if (seg.industrial == null) {
                        lines.push(
                          row.geoid === BURBANK_GEOID
                            ? 'SoCalGas industrial therms are filed for 2021–2024 only (not in CAP inventory).'
                            : 'Industrial therms are not reported separately for this year.'
                        );
                      }
                      return lines;
                    },
                  }
              : isElecNonresComponentChart
              ? {
                  filter: () => true,
                  label: () => '',
                  afterBody: (items) => {
                    const year = Number(items[0]?.label);
                    const row = rowByYear.get(year);
                    if (!row) return [];
                    const lines = elecNonresYearTooltipLines(row, mode);
                    if (isBurbankEiaPilot) {
                      lines.push(
                        `Data source: ${activityDataSourceLabelForEstimate(row.nonresidential, {
                          treatUtilityAsEia: row.nonresidential.sourceClass !== 'cap_published',
                        })}`
                      );
                    }
                    return lines;
                  },
                }
              : isGasTotal
                ? {
                    filter: () => true,
                    label: () => '',
                    afterBody: (items) => {
                      const year = Number(items[0]?.label);
                      const row = rowByYear.get(year);
                      if (!row) return [];
                      return gasTotalYearTooltipLinesRevised(row, mode, {
                        includeIndustrial: gasTotalIncludeIndustrial,
                      });
                    },
                  }
                : isElecTotal
                  ? {
                      filter: () => true,
                      label: () => '',
                      afterBody: (items) => {
                        const year = Number(items[0]?.label);
                        const row = rowByYear.get(year);
                        if (!row) return [];
                        return elecTotalYearTooltipLines(row, mode);
                      },
                    }
                  : {
                      label: (ctx) => {
                        const year = Number(ctx.label);
                        const row = rowByYear.get(year);
                        if (!row) return '';
                        const est =
                          sector === 'residential'
                            ? row.residential
                            : sector === 'nonresidential'
                              ? row.nonresidential
                              : row.total;
                        const fuel = view.startsWith('elec') ? 'electricity' : 'natural_gas';
                        const expDisplay =
                          mode === 'emissions'
                            ? `${formatMtCo2e(experimentalMain[ctx.dataIndex])} MT CO2e`
                            : isElecActivity
                              ? formatElectricityTooltipPrimary(experimentalMain[ctx.dataIndex])
                              : `${formatActivityUnits(experimentalMain[ctx.dataIndex])} ${est.unit}`;
                        const lines = [
                          `Experimental: ${expDisplay}`,
                          `Data source: ${activityDataSourceLabelForEstimate(est, {
                            treatUtilityAsEia: isBurbankEiaPilot && view.startsWith('elec'),
                          })}`,
                          `Coverage: ${est.coverage}`,
                          est.sourceNote,
                        ];
                        if (
                          isBurbankEiaPilot &&
                          view.startsWith('elec') &&
                          est.coverage === 'cap_published'
                        ) {
                          const eiaKwh =
                            sector === 'residential'
                              ? row.eia861ReferenceResidentialKwh
                              : sector === 'nonresidential'
                                ? row.eia861ReferenceNonresidentialKwh
                                : null;
                          if (eiaKwh != null && Number.isFinite(eiaKwh)) {
                            lines.push(
                              mode === 'emissions'
                                ? `EIA-861 BWP same year: ${formatMtCo2e(
                                    activityMtCo2e(
                                      {
                                        value: eiaKwh,
                                        unit: 'kWh',
                                        coverage: 'complete_observed',
                                        sourceClass: 'utility_observed',
                                        methodId: 'burbank_eia861_overlay',
                                        sourceNote: '',
                                        includedCategories: [],
                                        missingCategories: [],
                                      },
                                      'electricity'
                                    ) ?? 0
                                  )} MT CO2e`
                                : `EIA-861 BWP same year: ${formatElectricityTooltipPrimary(eiaKwh)}`
                            );
                          }
                        }
                        if (
                          isBurbankEiaPilot &&
                          view === 'gas_residential' &&
                          burbankSoCalGasResidentialOverlayYear(year)
                        ) {
                          const therms = row.legacyPhysicalResidential;
                          if (therms != null && Number.isFinite(therms)) {
                            lines.push(
                              mode === 'emissions'
                                ? `ClimatePlans SoCal Gas: ${formatMtCo2e(
                                    activityMtCo2e(
                                      {
                                        value: therms,
                                        unit: 'therms',
                                        coverage: 'complete_observed',
                                        sourceClass: 'utility_observed',
                                        methodId: 'burbank_gas_res_socalgas_climateplans_overlay',
                                        sourceNote: '',
                                        includedCategories: [],
                                        missingCategories: [],
                                      },
                                      'natural_gas'
                                    ) ?? 0
                                  )} MT CO2e`
                                : `ClimatePlans SoCal Gas: ${formatActivityUnits(therms)} therms`
                            );
                          }
                        }
                        if (
                          isBurbankEiaPilot &&
                          view === 'gas_nonresidential' &&
                          burbankSoCalGasCommercialOverlayYear(year)
                        ) {
                          const therms = row.legacyPhysicalCommercial;
                          if (therms != null && Number.isFinite(therms)) {
                            lines.push(
                              mode === 'emissions'
                                ? `ClimatePlans SoCal Gas commercial: ${formatMtCo2e(
                                    activityMtCo2e(
                                      {
                                        value: therms,
                                        unit: 'therms',
                                        coverage: 'complete_observed',
                                        sourceClass: 'utility_observed',
                                        methodId: 'burbank_gas_com_socalgas_climateplans_overlay',
                                        sourceNote: '',
                                        includedCategories: [],
                                        missingCategories: [],
                                      },
                                      'natural_gas'
                                    ) ?? 0
                                  )} MT CO2e`
                                : `ClimatePlans SoCal Gas commercial: ${formatActivityUnits(therms)} therms`
                            );
                          }
                        }
                        if (hasCapSectorReference) {
                          const capVal = capSectorReference[ctx.dataIndex];
                          if (capVal != null) {
                            lines.push(`Published CAP (Rincon 2024): ${capVal.toLocaleString()} ${est.unit}`);
                          }
                        }
                        if (mode === 'emissions') {
                          lines.push(experimentalEmissionFactorLabel(fuel));
                          lines.push(
                            fuel === 'electricity'
                              ? EXPERIMENTAL_ELECTRICITY_EMISSION_TOOLTIP
                              : EXPERIMENTAL_GAS_EMISSION_TOOLTIP
                          );
                        }
                        if (est.commercialComponent != null) {
                          lines.push(
                            `Commercial component: ${formatWhole(est.commercialComponent).toLocaleString()}`
                          );
                        }
                        if (est.industrialComponent != null) {
                          lines.push(
                            `Industrial component: ${formatWhole(est.industrialComponent).toLocaleString()}`
                          );
                        }
                        if (est.additionalNonresidentialComponent != null) {
                          lines.push(
                            `Additional/unallocated: ${formatWhole(est.additionalNonresidentialComponent).toLocaleString()}`
                          );
                        }
                        return lines;
                      },
                    }),
          },
        },
      },
      scales: {
        x: { stacked: true, title: { display: true, text: 'Year' } },
        y: {
          stacked: true,
          beginAtZero: true,
          title: { display: true, text: yLabel },
          ticks: {
            callback: (v) =>
              isElecActivity ? formatGwhAxisTickFromKwh(Number(v)) : Number(v).toLocaleString(),
          },
        },
      },
    },
  };
}

export function gasTotalChartDatasets(
  rows: CanonicalEnergyActivityYear[],
  mode: import('./elecNonresidentialChartSegments').ChartDisplayMode
) {
  const bundle = buildRev2ChartBundle(rows, 'gas_total', mode);
  const byLabel = (label: string) =>
    bundle.data.datasets.find((d) => d.label === label)?.data as Array<number | null>;
  return {
    residential:
      byLabel(HARMONIZED_TOTAL_RESIDENTIAL_LABEL) ?? byLabel('Experimental residential') ?? [],
    commercialCategory:
      byLabel(HARMONIZED_TOTAL_NONRESIDENTIAL_LABEL) ??
      byLabel('Experimental commercial category') ??
      [],
    capTotal: byLabel('CAP total') ?? [],
    years: rows.map((r) => r.year),
  };
}

export function elecTotalChartDatasets(
  rows: CanonicalEnergyActivityYear[],
  mode: import('./elecNonresidentialChartSegments').ChartDisplayMode
) {
  const bundle = buildRev2ChartBundle(rows, 'elec_total', mode);
  const byLabel = (label: string) =>
    bundle.data.datasets.find((d) => d.label === label)?.data as Array<number | null>;
  return {
    residential:
      byLabel(HARMONIZED_TOTAL_RESIDENTIAL_LABEL) ?? byLabel('Experimental residential') ?? [],
    nonresidential:
      byLabel(HARMONIZED_TOTAL_NONRESIDENTIAL_LABEL) ?? byLabel('Experimental nonresidential') ?? [],
    capTotal: byLabel('CAP total') ?? [],
    years: rows.map((r) => r.year),
  };
}

export function elecNonresidentialChartDatasets(
  rows: CanonicalEnergyActivityYear[],
  mode: import('./elecNonresidentialChartSegments').ChartDisplayMode
) {
  const bundle = buildRev2ChartBundle(rows, 'elec_nonresidential', mode);
  const byLabel = (label: string) =>
    bundle.data.datasets.find((d) => d.label === label)?.data as number[];
  return {
    commercial: byLabel('Commercial') ?? [],
    industrial: byLabel('Industrial') ?? [],
    undivided: byLabel('Nonresidential total — sector split unavailable') ?? [],
    years: rows.map((r) => r.year),
  };
}
